/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { distinctUntilChanged, switchMap, take, filter, skip, withLatestFrom } from 'rxjs/operators';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	UserPreferenceCategories,
} from '@touchplan/fancy-referencer';

import PreferencesStore, { PreferencesStorePersistingStrategy } from './preference-store.service';
import { AppState } from 'src/app/services/app-state.service';
import { PreferencesManagerBase } from 'src/app/app-data/interfaces/preferences-manager-base.interface';

import firebase from 'firebase/compat/app';

export interface IMISCProjectPreferences {
	crewSizeRoles: string;
}
const DEFAULT_MISC_PROJECT_PREFERENCES: IMISCProjectPreferences = {
	crewSizeRoles: '',
};

export interface IMISCProjectPreferencesManager
	extends IMISCProjectPreferences,
		PreferencesManagerBase<IMISCProjectPreferences> {}

@Injectable({
	providedIn: 'root',
})
export class IMISCProjectPreferencesStateService
	extends PreferencesStore<IMISCProjectPreferences>
	implements IMISCProjectPreferencesManager
{
	constructor(private fireDB: AngularFireDatabase, private appState: AppState) {
		super(DEFAULT_MISC_PROJECT_PREFERENCES, {
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6557
			// PreferencesStore should allow storing in localStorage with a dynamic prefix, such as projectId.
			persistingStrategy: PreferencesStorePersistingStrategy.IN_MEMORY,
			// END PLACEHOLDER REPLACE AS PART OF TP-6557
		});
		this.userAndProject$
			.pipe(
				switchMap(([userId, projectId]) =>
					// Take(1) prevents an infinite loop by ensuring Firebase data updates don't trigger redundant state updates.
					this.buildProjectUserPreferencesFirebaseObject(userId, projectId).valueChanges().pipe(take(1))
				)
			)
			.subscribe((projectUserPreferences) => {
				this.setPreferences(projectUserPreferences);
			});
		// This side effect will react to preferences state changes to store the data in Firebase
		this.persistPreferencesChanges(this.preferences$);
	}

	private _crewSizeRoles$ = this.select((state) => state.crewSizeRoles);
	private _projectId$ = new BehaviorSubject<string>(null);

	private userAndProject$ = combineLatest([
		this.appState.userId.pipe(distinctUntilChanged()),
		this._projectId$.pipe(distinctUntilChanged()),
	]).pipe(filter(([userId, projectId]) => !!userId && !!projectId));

	/**
	 * This method will store the preferences in the DB each time the preferences were updated
	 */
	private persistPreferencesChanges = this.effect<IMISCProjectPreferences>((preferences$) => {
		// skip(1) is used as a workaround to ignore the change triggered by loading the initial state of the preferences,
		// preventing the current values in the database from being cleared on the first load.
		return preferences$.pipe(skip(1)).pipe(
			withLatestFrom(this.userAndProject$),
			tapResponse(
				([preferences, [userId, projectId]]) => {
					this.buildProjectUserPreferencesFirebaseObject(userId, projectId).update(preferences);
				},
				(err) => {
					console.error(err);
				}
			)
		);
	});

	set crewSizeRoles(value: string) {
		this.setPreference('crewSizeRoles', value);
	}
	set crewSizeRoles$(value: Observable<string>) {
		this.setPreference('crewSizeRoles', value);
	}
	get crewSizeRoles(): string {
		return this.getPreference('crewSizeRoles') as string;
	}
	get crewSizeRoles$(): Observable<string> {
		return this._crewSizeRoles$;
	}

	set projectId(value: string) {
		this._projectId$.next(value);
	}
	get projectId(): string {
		return this._projectId$.getValue();
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6557
	// PreferencesStore should allow storing lists.
	static serializeListPreferences(list: string[]): string {
		if (list.length === 0) {
			return '';
		}
		return list.join(',');
	}

	static deserializeListPreferences(list: string): string[] {
		return list.split(',');
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6557

	private buildProjectUserPreferencesFirebaseObject(
		userId: string,
		projectId: string
	): AngularFireObject<IMISCProjectPreferences> {
		const userProjectPreferences = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			fancyFirebaseGetters.getProjectUserPreferencesPath(projectId, userId, UserPreferenceCategories.MISC),
			projectId
		);
		return this.fireDB.object<IMISCProjectPreferences>(userProjectPreferences);
	}

	ngOnDestroy(): void {
		// This service will probably never be destroyed, but just in case.
		this._projectId$.complete();
		super.ngOnDestroy();
	}
}
